import { useState, useEffect, useCallback } from 'react';
import { startEvaluation, getEvaluationResult } from '../utils/api';
import { EvaluationResult } from '../types';
import { useTranslation } from 'react-i18next';

// Props pour le hook
interface UseEvaluationProps {
  file: File | null;
  lang?: string;
}

// Retour du hook
interface UseEvaluationReturn {
  evaluationId: string | null;
  evaluationResult: EvaluationResult | null;
  isUploading: boolean;
  isEvaluating: boolean;
  progress: number;
  error: string | null;
  showScores: boolean;
  startEvaluation: () => Promise<void>;
  resetEvaluation: () => void;
}

export const useEvaluation = ({ file, lang: propLang }: UseEvaluationProps): UseEvaluationReturn => {
  const { t, i18n } = useTranslation();
  const [evaluationId, setEvaluationId] = useState<string | null>(null);
  const [evaluationResult, setEvaluationResult] = useState<EvaluationResult | null>(null);
  const [isUploading, setIsUploading] = useState<boolean>(false);
  const [isEvaluating, setIsEvaluating] = useState<boolean>(false);
  const [progress, setProgress] = useState<number>(0);
  const [error, setError] = useState<string | null>(null);
  const [showScores, setShowScores] = useState<boolean>(false);

  // Get current language from i18n
  const lang = propLang || i18n.language || 'fr';

  // Démarrer une évaluation
  const startEvaluationHandler = useCallback(async () => {
    if (!file) {
      setError(t('evaluation.error', { error: t('fileUpload.error') }));
      return;
    }

    setIsUploading(true);
    setError(null);
    setProgress(0);
    setShowScores(false);

    try {
      const { evaluationId } = await startEvaluation(file, lang);
      setEvaluationId(evaluationId);
      setIsEvaluating(true);
      setIsUploading(false);
    } catch (err) {
      setError(t('evaluation.error', { error: err }));
      setIsUploading(false);
    }
  }, [file, lang, t]);

  // Polling pour les résultats de l'évaluation
  useEffect(() => {
    let intervalId: NodeJS.Timeout | null = null;

    if (evaluationId && !evaluationResult) {
      intervalId = setInterval(async () => {
        try {
          const result = await getEvaluationResult(evaluationId);

          if (result.status === 'completed' || result.status === 'finished') {
            if (result.prediction !== null && result.prediction_list !== null) {
              setEvaluationResult({
                prediction: result.prediction,
                prediction_list: result.prediction_list,
                status: result.status,
              });
              setIsEvaluating(false);
              setShowScores(true);
              if (intervalId) clearInterval(intervalId);
            }
          } else if (result.status === 'failed') {
            setError(t('evaluation.retryError'));
            setIsEvaluating(false);
            if (intervalId) clearInterval(intervalId);
          }
        } catch (err) {
          setError(t('evaluation.fetchError', { error: err }));
          if (intervalId) clearInterval(intervalId);
        }
      }, 2000);
    }

    return () => {
      if (intervalId) clearInterval(intervalId);
    };
  }, [evaluationId, evaluationResult, t]);

  // Simuler la progression de l'upload
  useEffect(() => {
    let intervalId: NodeJS.Timeout | null = null;
    
    if (isUploading) {
      intervalId = setInterval(() => {
        setProgress(prev => {
          if (prev >= 100) {
            clearInterval(intervalId!);
            return 100;
          }
          return prev + 10;
        });
      }, 300);
    }

    return () => {
      if (intervalId) clearInterval(intervalId);
    };
  }, [isUploading]);

  // Réinitialiser l'évaluation
  const resetEvaluation = useCallback(() => {
    setEvaluationId(null);
    setEvaluationResult(null);
    setError(null);
    setProgress(0);
    setIsEvaluating(false);
    setShowScores(false);
  }, []);

  return {
    evaluationId,
    evaluationResult,
    isUploading,
    isEvaluating,
    progress,
    error,
    showScores,
    startEvaluation: startEvaluationHandler,
    resetEvaluation,
  };
};
